import { prop, path } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { dispatcher } from 'dispatcher';
import * as oldServer from '@theme/js/hooks/user/server';
import { useUserSelector } from '@core/js/store/hooks';
import * as actions from '@theme/js/hooks/user/store/actions';
import { parseUserDetails } from '@core/js/hooks/user/helpers';
import { usePlayerFields } from '@core/js/hooks/usePlayerFields';
import { useIsAdmin } from '@core/js/hooks/user';
import { useFetchParams, useFetchBalance } from '@core/js/hooks/user/hooks';
import { setContactPreferences } from '@core/js/components/main/ContactPreferences/actions';
import { useChangePassword, useUpdatePlayerInfo, useValidatePassword } from 'server';

import * as hooks from '@core/js/hooks/usePublicAPI/hooks/useUserPublicAPI/hooks';

const notLoggedIn = () => Promise.reject(new Error('User Public API: User must be logged in.'));

export const setPlayerTags = isLoggedIn => (tags = [], isForce) => {
    if (!isLoggedIn && !isForce) {
        return notLoggedIn();
    }

    return oldServer.setPlayerTags(tags);
};

export const getPlayerTags = isLoggedIn => (tagName, isForce) => {
    if (!isLoggedIn && !isForce) {
        return notLoggedIn();
    }

    return oldServer.getPlayerTags(tagName);
};

export const removePlayerTags = isLoggedIn => (tagName, isForce) => {
    if (!isLoggedIn && !isForce) {
        return notLoggedIn();
    }

    return oldServer.removePlayerTags(tagName);
};

const fetchUserDetails = (isLoggedIn, networkTypes, networkDataFilters, defaultPlayerFields) => async (playerFields = []) => {
    if (!isLoggedIn) {
        return notLoggedIn();
    }

    const fields = playerFields?.length ? playerFields : defaultPlayerFields;
    const response = await oldServer.fetchUserDetails(fields, networkTypes, networkDataFilters);

    return { data: parseUserDetails(response.data) };
};

const setCountry = dispatch => country => dispatch(actions.setCountry(country));

const useMobilePushPreference = () => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));

    const contactPreferences = useUserSelector(path(['userDetails', 'contactPreferences'])) || [];
    const mobilePush = contactPreferences
        .filter(({ channel, type }) => channel === 'mobilePush' && type === 'account');

    return useCallback((data) => {
        if (!isLoggedIn) {
            throw new Error('User Public API: User must be logged in.');
        }
        const updatedMobilePush = mobilePush.map(item => ({
            ...item,
            preference: data
        }));
        setContactPreferences(updatedMobilePush);
        dispatcher.dispatch('user:details:update', updatedMobilePush);
    }, [isLoggedIn, mobilePush]);
};

export const useUserPublicAPI = () => {
    const dispatch = useDispatch();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const { networkTypes, networkDataFilters } = useFetchParams();
    const defaultPlayerFields = usePlayerFields();
    const fetchBalance = useFetchBalance();
    const getGeoData = hooks.useGetGeoData();
    const changePassword = useChangePassword();
    const updatePlayerInfo = useUpdatePlayerInfo();
    const validatePassword = useValidatePassword();
    const isAdmin = useIsAdmin();
    const setMobilePushPreferences = useMobilePushPreference();

    return useMemo(() => ({
        /**
         * Retrieve all player tags or a specific one.
         * @memberOf Playtech.API.user
         * @function
         * @param [tagName] {String} If set, retrieve only this tag.
         * @param [isForce] {Boolean} If set, try to retrieve tag before login.
         * @returns {Promise<{ID: number, data: {correlationId: String, tags: Array<{name: String, type: String, value: String, expirationDate: String}>}}>}
         * @example
         * Playtech.API.user.getPlayerTags('Playertag', true);
         */
        getPlayerTags: getPlayerTags(isLoggedIn),
        /**
         * Set tags for a current player.
         * @memberOf Playtech.API.user
         * @function
         * @param [tags=[]] {Array} List of tags.
         * @param [isForce] {Boolean} If set, try to set tag before login.
         * @returns {Promise<{ID: number, data: {correlationId: String, tags: Array<{name: String, type: String, value: String, expirationDate: String}>}}>}
         * @example
         * Playtech.API.user.setPlayerTags(
         *      [{
         *         name: 'tag1',
         *         value: 'value1',
         *         type: 'text',
         *         expirationDate: '2024-04-13'
         *      }],
         *      true
         * );
         */
        setPlayerTags: setPlayerTags(isLoggedIn),
        /**
         * Get player data.
         * @memberOf Playtech.API.user
         * @function
         * @param [playerFields=[defaults...]] {Number[]}
         * @returns {{data: Object}}
         * @example
         * Playtech.API.user.getPlayerData([1]);
         */
        getPlayerData: fetchUserDetails(isLoggedIn, networkTypes, networkDataFilters, defaultPlayerFields),
        /**
         * Set country.
         * @memberOf Playtech.API.user
         * @function
         * @param country {String}
         * @example
         * Playtech.API.user.setCountry('UA');
         */
        setCountry: setCountry(dispatch),
        /**
         * Retrieve player balances based on "Subscription balance types" and updates user's balance in store.
         * @memberOf Playtech.API.user
         * @function
         * @returns {Promise<{data: {balances: Array<{balanceType: String, balance: {amount: String, currencyCode: String}}>}}>}
         * @example
         * Playtech.API.user.fetchBalance();
         */
        fetchBalance,
        /**
         * Return whether the user is logged.
         * @memberOf Playtech.API.user
         * @function
         * @returns {Boolean}
         * @example
         * Playtech.API.user.isLoggedIn();
         */
        isLoggedIn: () => isLoggedIn,
        /**
         * Retrieve player's geo data.
         * @memberOf Playtech.API.user
         * @function
         * @returns {Promise<{country: string, state: string, city: string, latitude: string, longitude: string, accuracyRadius: string}>}
         * @example
         * Playtech.API.user.getGeoData();
         */
        getGeoData,
        /**
         * Validate player's password.
         * @memberOf Playtech.API.user
         * @function
         * @returns {Promise<void>}
         * @example
         * Playtech.API.user.validatePassword({ password });
         */
        validatePassword,
        /**
         * Change player's password.
         * @memberOf Playtech.API.user
         * @function
         * @returns {Promise<void>}
         * @example
         * Playtech.API.user.changePassword({ newPassword, oldPassword });
         */
        changePassword,
        /**
         * Update player's information.
         * @memberOf Playtech.API.user
         * @function
         * @returns {Promise<void>}
         * @example
         * Playtech.API.user.updatePlayerInfo({ dataMap, addressDetails });
         */
        updatePlayerInfo,
                /**
         * Set tags for a current player.
         * @memberOf Playtech.API.user
         * @function
         * @param [tags=[]] {Array} List of tags.
         * @param [isForce] {Boolean} If set, try to set tag before login.
         * @example
         * Playtech.API.user.removePlayerTags(tags);
         */
        removePlayerTags: removePlayerTags(isLoggedIn), // ~(TotalCasino Changes)~
        /**
         * Updates userTags in store
         * @memberOf Playtech.API.user
         * @function
         * @param [tags] {Array} list of tags
         * @example
         * Playtech.API.user.updatePlayerTags(tags);
         */
        updatePlayerTags: (tags) => {
            dispatch(actions.setUserTagsAction(tags)); // ~(TotalCasino Changes)~
        },
        isAdmin, // ~(TotalCasino Changes)~
        setMobilePushPreferences // ~(TotalCasino Changes)~
    }), [
        dispatch,
        isLoggedIn,
        getGeoData,
        networkTypes,
        fetchBalance,
        changePassword,
        updatePlayerInfo,
        validatePassword,
        networkDataFilters,
        defaultPlayerFields,
        isAdmin,
        setMobilePushPreferences
    ]);
};
